import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Banner, Button, Card, EmptyState, Field, Mono, PageBody, PageHeader } from "../ui/Field";

type Var = { key: string; value: string; updated_at: number };

export default function Variables() {
  const { slug } = useParams();
  const [items, setItems] = useState<Var[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const load = () => api.get<{ items: Var[] }>(`/api/sites/${slug}/variables`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.put(`/api/sites/${slug}/variables/${encodeURIComponent(newKey)}`, { value: newValue });
      setNewKey(""); setNewValue("");
      load();
    } catch (e) { setErr((e as Error).message); }
  }
  async function update(k: string, v: string) {
    try { await api.put(`/api/sites/${slug}/variables/${encodeURIComponent(k)}`, { value: v }); load(); }
    catch (e) { setErr((e as Error).message); }
  }
  async function remove(k: string) {
    if (!confirm(`Remove variable "${k}"?`)) return;
    try { await api.delete(`/api/sites/${slug}/variables/${encodeURIComponent(k)}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 08 · Variables"
        title={<>Reusable <em>values</em></>}
        description="Phone numbers, social links, copy snippets — interpolated into any block prop as {{site.key}}."
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="V1" title="Add variable">
        <form onSubmit={add} className="grid grid-cols-[200px_1fr_100px] gap-3 items-end">
          <Field label="Key" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="phoneNumber" required pattern="[a-zA-Z][a-zA-Z0-9_]*" className="mb-0" />
          <Field label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} required className="mb-0" />
          <Button type="submit" className="h-9">Add</Button>
        </form>
      </Card>

      <Card number="V2" title={`Variables (${items.length})`} className="mt-6" bodyClassName="p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title="No variables yet." description="Variables let you avoid copy-pasting the same string across pages." /></div>
        ) : (
          <div className="divide-y divide-[var(--color-rule)]">
            {items.map((v) => (
              <div key={v.key} className="grid grid-cols-[260px_1fr_80px] gap-4 items-center px-5 py-3">
                <Mono className="text-[13px] text-[var(--color-accent)]">{`{{site.${v.key}}}`}</Mono>
                <input
                  defaultValue={v.value}
                  onBlur={(e) => { if (e.target.value !== v.value) update(v.key, e.target.value); }}
                  className="w-full px-2 py-1 text-[13.5px] border border-transparent hover:border-[var(--color-rule)] focus:border-[var(--color-ink)] rounded-[3px] bg-transparent focus:bg-[var(--color-surface)] transition-colors focus:outline-none"
                />
                <Button variant="ghost" size="sm" onClick={() => remove(v.key)}>Remove</Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PageBody>
  );
}
